import React, { useEffect, useRef, useState } from 'react';
import { Button, Popup } from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';
import { Copy, Check } from 'lucide-react';

interface CopyButtonProps {
  value: string;
  size?: number;
}

export const CopyButton: React.FC<CopyButtonProps> = ({
  value,
  size = 16
}) => {
  const [copied, setCopied] = useState(false);
  const resetTimeout = useRef<ReturnType<typeof setTimeout>>();

  // Clear the pending reset on unmount so it cannot set state afterwards.
  useEffect(() => () => clearTimeout(resetTimeout.current), []);

  if (!value) {
    return null;
  }

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = value;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    setCopied(true);
    clearTimeout(resetTimeout.current);
    resetTimeout.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Popup
      content={copied ? I18n.t('general.copied') : I18n.t('general.copy')}
      position="top center"
      open={copied ? true : undefined}
      trigger={
        <Button
          compact
          size="mini"
          icon
          onClick={handleCopy}
          aria-label={I18n.t('general.copy')}
          style={{
            padding: '4px',
            minHeight: 'auto',
            border: 'none',
            boxShadow: 'none',
            backgroundColor: 'transparent'
          }}
        >
          {copied ? (
            <Check size={size} style={{ color: 'var(--gr-green)' }} />
          ) : (
            <Copy size={size} style={{ color: 'var(--text-secondary)' }} />
          )}
        </Button>
      }
    />
  );
};

export default CopyButton;
